import { Link } from "react-router-dom";
import { HeartHandshake, Sparkles, Wheat } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/lib/SiteContext";

const VALUES = [
  { icon: HeartHandshake, title: "Handmade, always", text: "Every brownie, blondie and cookie is made by hand in small batches — no production lines, no shortcuts." },
  { icon: Sparkles, title: "Quality ingredients", text: "Proper chocolate, real butter and careful sourcing. If we wouldn't serve it to our family, we won't sell it to yours." },
  { icon: Wheat, title: "Honest about allergens", text: "Clear allergen information on every product, and a genuine invitation to talk to us before you order." },
];

export default function About() {
  const { site } = useSite();
  const c = site?.content?.about || {};
  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8" data-testid="about-page">
      <Reveal>
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold">Our Story</p>
          <h1 className="mt-3 font-serif text-3xl font-bold tracking-tight text-cream-text sm:text-4xl lg:text-5xl">
            {c.heading || "From our family kitchen to yours"}
          </h1>
          <p className="mt-6 whitespace-pre-line text-base leading-relaxed text-cream-muted sm:text-lg">
            {c.body || "We started making Christmas treats for family and friends, sharing the love with every bite. Now we want to share it with you! Francisco, our chef, brings over 20 years of experience to the team, along with a real sweet tooth!"}
          </p>
          <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-cream-muted sm:text-lg">
            {c.secondary_body || "Let us provide the bakes for your next birthday, celebration or office gathering — and find out what all of the hype is about."}
          </p>
        </div>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
        {VALUES.map((v, i) => (
          <Reveal key={v.title} delay={i * 0.06}>
            <div className="h-full rounded-3xl bg-cream p-6 text-cream-darktext sm:p-8" data-testid={`value-card-${i}`}>
              <v.icon className="h-7 w-7 text-gold" aria-hidden="true" />
              <h2 className="mt-4 font-serif text-xl font-semibold">{v.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-cream-darkmuted">{v.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-14">
        <div className="rounded-3xl border border-gold/40 bg-choco-light p-6 sm:p-8" data-testid="about-allergen-note">
          <h2 className="font-serif text-xl font-semibold text-cream-text">Allergies? Talk to us first.</h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-cream-muted">
            Our kitchen handles common allergens. If you or someone you're ordering for has an allergy,
            please contact us before placing an order so we can advise you properly.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/allergens" className="text-sm font-bold text-gold hover:underline" data-testid="about-allergens-link">
              Read our allergen information
            </Link>
            <Link to="/contact" className="text-sm font-bold text-gold hover:underline" data-testid="about-contact-link">
              Contact us
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
